const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

// Caches geocoding results in memory for the life of the page session.
// This is the fix for the real cost/performance issue: without it,
// buildOrderedRoute() re-geocodes every stop's address on EVERY GPS
// position update while a route is active — even though a load's
// pickup/dropoff addresses never change mid-route. With this cache,
// each unique address only ever gets geocoded ONCE per session, no
// matter how many times the route recalculates afterward.
const geocodeCache = new Map();

// Converts a plain address string (e.g. "Dallas, TX") into { lat, lng }
// using Mapbox's Geocoding API. Checks the in-memory cache first.
export async function geocodeAddress(address) {
  const cacheKey = address.trim().toLowerCase();

  if (geocodeCache.has(cacheKey)) {
    return geocodeCache.get(cacheKey);
  }

  const encodedAddress = encodeURIComponent(address);
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedAddress}.json?access_token=${MAPBOX_TOKEN}&limit=1`;

  const response = await fetch(url);
  const data = await response.json();

  let result = null;
  if (data.features && data.features.length > 0) {
    const [lng, lat] = data.features[0].center; // Mapbox returns [lng, lat] order
    result = { lat, lng };
  }

  // Cache the result even if geocoding failed (null) — prevents
  // repeatedly retrying a bad/unresolvable address on every tick too.
  geocodeCache.set(cacheKey, result);

  return result;
}

// Straight-line distance between two coordinates, in miles.
// Used for ordering stops by proximity — doesn't need to be driving
// distance for the ORDERING decision itself, just relative closeness.
// (The actual displayed ROUTE still uses real driving directions.)
export function haversineDistance(coord1, coord2) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 3958.8; // Earth's radius in miles

  const dLat = toRad(coord2.lat - coord1.lat);
  const dLng = toRad(coord2.lng - coord1.lng);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(coord1.lat)) * Math.cos(toRad(coord2.lat)) * Math.sin(dLng / 2) ** 2;

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}